/**
 * The block registry — the single source every other layer reads.
 *
 * A registry entry is pure metadata (no React), so validation, the editor form, the AI tool
 * definitions, the publish-time validator and the seeds all import from here without pulling in a
 * renderer. The render COMPONENT that implements each entry's props contract is keyed separately in
 * `@ksa/render`, so this file stays DOM-free and the Worker and the API can use it as-is.
 *
 * **Adding a block is: a `BlockDef` here + a render component there + one line in each map.** No new
 * editor code, no new form, no new validator branch, no new AI prompt. If adding a block requires
 * any of those, the field vocabulary in `prop-types.ts` is missing something and *that* is what to
 * fix.
 */

import type { BlockDef } from '../prop-types.ts'
import { navbar } from './blocks/navbar.ts'
import { footer } from './blocks/footer.ts'
import { hero } from './blocks/hero.ts'
import { heading } from './blocks/heading.ts'
import { text } from './blocks/text.ts'
import { image } from './blocks/image.ts'
import { gallery } from './blocks/gallery.ts'
import { features } from './blocks/features.ts'
import { team } from './blocks/team.ts'
import { cta } from './blocks/cta.ts'
import { contact } from './blocks/contact.ts'
import { faq } from './blocks/faq.ts'
import { testimonials } from './blocks/testimonials.ts'

const ENTRIES: BlockDef[] = [
  navbar,
  footer,
  hero,
  heading,
  text,
  image,
  gallery,
  features,
  team,
  cta,
  contact,
  faq,
  testimonials,
]

export const REGISTRY: Record<string, BlockDef> = Object.fromEntries(ENTRIES.map((b) => [b.type, b]))

export function getBlockDef(type: string): BlockDef | undefined {
  return REGISTRY[type]
}

export function allBlockDefs(): BlockDef[] {
  return ENTRIES
}

/** The page blocks a customer may add from the palette. Site-scoped singletons (navbar, footer) are
 *  excluded — they are created with the site, not added to a page. */
export function pageBlockDefs(): BlockDef[] {
  return ENTRIES.filter((b) => b.scope === 'page')
}

/**
 * The compact index the AI router sees FIRST — name and one line each — before being sent the full
 * schema of only the blocks it picked.
 *
 * ⚠️ TWO STAGES, AND THE REASON IS COST AND ACCURACY IN THE SAME MOVE. Thirteen full block schemas is
 * several thousand tokens on every request, most of it describing blocks the model will not use; and
 * a model handed thirteen similar schemas at once picks `hero` for everything. A short index first
 * makes the *choice* the model's whole job, and the second call makes filling one in its whole job.
 */
export function registryIndex(): { type: string; description: string; whenToUse: string; whenNotToUse: string }[] {
  return ENTRIES.map((b) => ({
    type: b.type,
    description: b.description,
    whenToUse: b.whenToUse,
    whenNotToUse: b.whenNotToUse,
  }))
}
